<template>
	<Teleport to="body">
		<TooltipPanel
			v-if="activeTooltip"
			ref="panelRef"
			:text="activeTooltip.text"
			:html="activeTooltip.html"
			:color="activeTooltip.color"
			:placement="activeTooltip.placement"
			:resolved-placement="resolvedPlacement"
			:multiline="activeTooltip.multiline"
			:max-width="activeTooltip.maxWidth"
			:show-arrow="activeTooltip.showArrow"
			:actions="activeTooltip.actions"
			:disabled="activeTooltip.disabled"
			:open="true"
			:x="coordinates.x"
			:y="coordinates.y"
			@action-click="handleActionClick"
			@hover-change="handleHoverChange"
		>
			<template v-if="activeTooltip.renderContent" #content>
				<RenderContent :render-content="activeTooltip.renderContent" />
			</template>
		</TooltipPanel>
	</Teleport>
</template>

<script lang="ts" setup>
import {
	computed,
	defineComponent,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
	type PropType,
	type VNodeChild,
} from 'vue';
import TooltipPanel from './TooltipPanel.vue';
import { tooltipService } from './Tooltip.service';
import type {
	TooltipAction,
	TooltipPlacement,
	TooltipRenderContent,
} from './Tooltip.model';

const GAP = 8;
const VIEWPORT_PADDING = 8;

const RenderContent = defineComponent({
	name: 'TooltipRenderContent',
	props: {
		renderContent: {
			// TooltipRenderContent is itself a function type, so PropType resolves to
			// a factory signature and the direct cast is rejected under strict mode.
			type: Function as unknown as PropType<TooltipRenderContent | null>,
			default: null,
		},
	},
	setup(props) {
		return () =>
			props.renderContent ? (props.renderContent() as VNodeChild) : null;
	},
});

const panelRef = ref<InstanceType<typeof TooltipPanel> | null>(null);
const coordinates = ref({ x: 0, y: 0 });
const resolvedPlacement = ref<TooltipPlacement>('top');

const activeTooltip = computed(() => tooltipService.tooltip.value);

const clamp = (value: number, min: number, max: number) =>
	Math.min(Math.max(value, min), max);

const getPanelElement = () => panelRef.value?.panelRef || null;

const getPositionForPlacement = (
	placement: TooltipPlacement,
	anchorRect: DOMRect,
	panelRect: DOMRect
) => {
	const centerX = anchorRect.left + anchorRect.width / 2;
	const centerY = anchorRect.top + anchorRect.height / 2;

	switch (placement) {
		case 'bottom':
			return {
				x: centerX,
				y: anchorRect.bottom + GAP,
			};
		case 'left':
			return {
				x: anchorRect.left - panelRect.width - GAP,
				y: centerY,
			};
		case 'right':
			return {
				x: anchorRect.right + GAP,
				y: centerY,
			};
		case 'top':
		default:
			return {
				x: centerX,
				y: anchorRect.top - panelRect.height - GAP,
			};
	}
};

const getPanelBounds = (
	position: { x: number; y: number },
	panelRect: DOMRect,
	placement: TooltipPlacement
) => {
	switch (placement) {
		case 'bottom':
		case 'top':
			return {
				left: position.x - panelRect.width / 2,
				right: position.x + panelRect.width / 2,
				top: position.y,
				bottom: position.y + panelRect.height,
			};
		case 'left':
		case 'right':
		default:
			return {
				left: position.x,
				right: position.x + panelRect.width,
				top: position.y - panelRect.height / 2,
				bottom: position.y + panelRect.height / 2,
			};
	}
};

const fitsViewport = (
	position: { x: number; y: number },
	panelRect: DOMRect,
	placement: TooltipPlacement
) => {
	const bounds = getPanelBounds(position, panelRect, placement);

	return (
		bounds.left >= VIEWPORT_PADDING &&
		bounds.top >= VIEWPORT_PADDING &&
		bounds.right <= window.innerWidth - VIEWPORT_PADDING &&
		bounds.bottom <= window.innerHeight - VIEWPORT_PADDING
	);
};

const getPlacementOrder = (preferred: TooltipPlacement): TooltipPlacement[] => {
	const oppositeMap: Record<TooltipPlacement, TooltipPlacement> = {
		top: 'bottom',
		bottom: 'top',
		left: 'right',
		right: 'left',
	};

	const order: TooltipPlacement[] = [
		preferred,
		oppositeMap[preferred],
		'top',
		'bottom',
		'right',
		'left',
	];

	return order.filter((placement, index, list) => list.indexOf(placement) === index);
};

const updatePosition = () => {
	const tooltip = activeTooltip.value;
	if (!tooltip?.anchor || !document.body.contains(tooltip.anchor)) {
		tooltipService.close(tooltip?.id);
		return;
	}

	const panelElement = getPanelElement();
	if (!panelElement) {
		return;
	}

	const anchorRect = tooltip.anchor.getBoundingClientRect();
	const panelRect = panelElement.getBoundingClientRect();
	const placementOrder = getPlacementOrder(tooltip.placement || 'top');

	let nextPlacement = placementOrder[0];
	let nextPosition = getPositionForPlacement(
		nextPlacement,
		anchorRect,
		panelRect
	);

	for (const placement of placementOrder) {
		const candidate = getPositionForPlacement(placement, anchorRect, panelRect);
		if (fitsViewport(candidate, panelRect, placement)) {
			nextPlacement = placement;
			nextPosition = candidate;
			break;
		}
	}

	resolvedPlacement.value = nextPlacement;
	if (nextPlacement === 'top' || nextPlacement === 'bottom') {
		coordinates.value = {
			x: clamp(
				nextPosition.x,
				VIEWPORT_PADDING + panelRect.width / 2,
				window.innerWidth - panelRect.width / 2 - VIEWPORT_PADDING
			),
			y: clamp(
				nextPosition.y,
				VIEWPORT_PADDING,
				window.innerHeight - panelRect.height - VIEWPORT_PADDING
			),
		};
		return;
	}

	coordinates.value = {
		x: clamp(
			nextPosition.x,
			VIEWPORT_PADDING,
			window.innerWidth - panelRect.width - VIEWPORT_PADDING
		),
		y: clamp(
			nextPosition.y,
			VIEWPORT_PADDING + panelRect.height / 2,
			window.innerHeight - panelRect.height / 2 - VIEWPORT_PADDING
		),
	};
};

const schedulePositionUpdate = () => {
	nextTick(() => {
		updatePosition();
	});
};

const handleActionClick = (action: TooltipAction) => {
	activeTooltip.value?.onActionClick?.(action);
};

const handleHoverChange = (hovered: boolean) => {
	activeTooltip.value?.onHoverChange?.(hovered);
};

const handleViewportChange = () => {
	if (!activeTooltip.value) {
		return;
	}

	schedulePositionUpdate();
};

onMounted(() => {
	document.addEventListener('scroll', handleViewportChange, true);
	window.addEventListener('resize', handleViewportChange);
});

onBeforeUnmount(() => {
	document.removeEventListener('scroll', handleViewportChange, true);
	window.removeEventListener('resize', handleViewportChange);
	tooltipService.clear();
});

watch(
	activeTooltip,
	(tooltip) => {
		if (!tooltip) {
			return;
		}

		schedulePositionUpdate();
	},
	{ deep: true }
);
</script>
